<template>
  <v-row v-if="list.length > 0 || (showSpecialOnEmptyList && special)" no-gutters class="pt-6 pb-6">
    <v-col cols="12" sm="4" class="pr-0 pr-sm-6">
      <v-row no-gutters>
        <v-col cols="12">
          <p class="mb-3 f-20 sz-primary-grey--text font-weight-medium">
            <v-icon v-if="icon" :size="25" color="szRed" class="mb-1 mr-1">
              {{ icon }}
            </v-icon>
            {{ title }}
          </p>
        </v-col>
        <v-col
          v-if="special"
          cols="12"
          class="sz-primary-background pa-3 pa-sm-5 d-none d-sm-block d-print-none"
        >
          <p
            v-if="list.length > 0"
            class="mb-0 f-16 font-weight-medium sz-primary-text--grey wrap-anywhere pre-wrap"
          >
            {{ specialRequirementsTitle }}:
          </p>
          <p
            class="ml-1 mb-0 f-16 font-weight-regular sz-primary-text--grey wrap-anywhere pre-wrap"
          >
            {{ special }}
          </p>
        </v-col>
      </v-row>
    </v-col>
    <v-col v-if="list.length > 0" cols="12" sm="8" class="pl-0 pl-sm-6 mt-3 mt-sm-0">
      <v-row no-gutters>
        <v-col
          v-for="(item, index) in list"
          :key="index"
          cols="12"
          sm="6"
          class="needs-border-right pb-2 col-print-4"
        >
          <p
            v-if="getText(item)"
            class="ml-4 mb-0 f-16 sz-primary-grey--text font-weight-regular d-inline-block wrap-anywhere pre-wrap"
          >
            <span class="d-inline-block d-md-none d-print-none mr-1">•</span>
            <span class="d-none d-print-inline-block f-32 mr-1">□ </span>
            <span>{{ getText(item) }}</span>
            <span
              v-if="other && index === 0"
              class="mb-0 ml-1 f-16 sz-primary-grey--text font-weight-medium d-inline-block pre-wrap"
            >
              {{ other }}
            </span>
          </p>
          <p
            v-if="getGift(item)"
            class="ml-4 mb-0 f-16 sz-primary-grey--text font-weight-medium wrap-anywhere pre-wrap"
          >
            <span class="d-inline-block d-md-none d-print-none mr-1">•</span>
            <span class="d-none d-print-inline-block f-32 mr-1">□ </span>
            <span class="d-inline-block">{{ getGift(item)?.name }}: </span>
            <span class="font-weight-regular pre-wrap">{{ getGift(item)?.text }}</span>
          </p>
          <div v-if="getClothing(item)">
            <div v-for="(personItem, index) in getClothing(item)?.item" :key="index">
              <div
                class="ml-4 mb-0 f-16 sz-primary-grey--text font-weight-medium wrap-anywhere pre-wrap"
              >
                <span class="d-inline-block d-md-none d-print-none mr-1">•</span>
                <span class="d-none d-print-inline-block f-32 mr-1">□ </span>
                <span class="d-inline-block">{{ getClothing(item)?.name }}:</span>
                <div class="font-weight-regular pre-wrap ml-2" v-orphans>
                  {{ itemType(personItem) }}
                </div>
                <div class="f-14 mb-0 sz-secondary-grey--text font-weight-regular ml-2" v-orphans>
                  {{ t('label.size') }}: {{ personItem.size }}
                  <span v-if="personItem.note"
                    >, {{ t('label.comments') }}:{{
                      getNoteWithNonBreakingSpace(personItem.note)
                    }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-col>
    <v-col
      v-if="special != null"
      cols="12"
      class="sz-primary-background pa-3 pa-sm-5 d-print-block d-flex d-sm-none"
    >
      <p v-if="list.length > 0" class="mb-0 f-16 font-weight-medium sz-primary-text--grey">
        {{ t('label.comments') }}:
      </p>
      <p class="mb-0 ml-5 f-16 font-weight-regular sz-primary-text--grey wrap-anywhere pre-wrap">
        {{ special }}
      </p>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { UNICODE } from '@/constants/unicode'
import type {
  ClothingItem,
  GiftItem,
  ClothingListItem,
  NeedItem,
  GiftDisplay,
  ClothingDisplay
} from '@/types'

type Props = {
  title: string
  list: NeedItem[]
  special?: string | null
  showSpecialOnEmptyList?: boolean
  other?: string | null
  icon?: string | null
  specialRequirements?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  special: null,
  showSpecialOnEmptyList: false,
  other: null,
  icon: null,
  specialRequirements: true
})

function isGiftItem(item: object): item is GiftItem {
  return 'gift' in item
}

function isClothingListItem(item: object): item is ClothingListItem {
  return 'chosenList' in item
}

const { t } = useI18n()

const specialRequirementsTitle = computed(() =>
  props.specialRequirements ? t('label.specialRequirements') : t('label.comments')
)

function getNoteWithNonBreakingSpace(note: string): string {
  return UNICODE.NON_BREAKING_SPACE + note
}

function itemType(item: ClothingItem): string {
  if ('type' in item) {
    return `${item.category}${UNICODE.NON_BREAKING_SPACE}(${item.type})`
  }
  return item.category
}

function getText(item: NeedItem): string | null {
  return typeof item === 'string' ? item : null
}

function getGift(item: NeedItem): GiftDisplay | null {
  if (typeof item === 'object' && isGiftItem(item)) {
    return { text: item.gift, name: item.newName }
  }
  return null
}

function getClothing(item: NeedItem): ClothingDisplay | null {
  if (typeof item === 'object' && isClothingListItem(item)) {
    return { item: item.chosenList, name: item.newName }
  }
  return null
}
</script>
